/** JSON body fields for add/update subtask (repo URL → commit hashes). */
export type SubtaskCommitHashesMap = Record<string, string[]>;

export type SubtaskExtrasBody = {
  verificationEndpoints?: string[];
  commitHashes?: SubtaskCommitHashesMap;
};

export function nonEmptyVerificationList(text: string): string[] | null {
  const list = text
    .split(/[\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return list.length ? list : null;
}

export function verificationEndpointsToText(raw: unknown): string {
  if (raw == null) return "";
  if (Array.isArray(raw)) {
    return raw
      .map((x) => String(x).trim())
      .filter((s) => s.length > 0)
      .join("\n");
  }
  return String(raw);
}

export function parseVerificationEndpoints(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  const text = String(raw).trim();
  if (!text) return [];
  return text
    .split(/[\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export type SubtaskCommitHashGroup = {
  repoUrl: string;
  hashes: string[];
};

export function parseCommitHashesForDisplay(
  raw: unknown,
): SubtaskCommitHashGroup[] {
  if (raw == null) return [];

  if (typeof raw === "object" && !Array.isArray(raw)) {
    return Object.entries(raw as Record<string, unknown>)
      .map(([repoUrl, value]) => ({
        repoUrl,
        hashes: normalizeHashesList(value),
      }))
      .filter((entry) => entry.hashes.length > 0);
  }

  if (Array.isArray(raw)) {
    return raw
      .map((entry, index) => {
        if (Array.isArray(entry)) {
          return {
            repoUrl: `Repository ${index + 1}`,
            hashes: normalizeHashesList(entry),
          };
        }
        if (typeof entry === "string" && entry.trim()) {
          return {
            repoUrl: `Repository ${index + 1}`,
            hashes: entry
              .split(/[\n\r,]+/)
              .map((s) => s.trim())
              .filter(Boolean)
              .map((h) => h.slice(0, 64)),
          };
        }
        return { repoUrl: `Repository ${index + 1}`, hashes: [] as string[] };
      })
      .filter((entry) => entry.hashes.length > 0);
  }

  return [];
}

function normalizeHashesList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => String(x).trim())
    .filter(Boolean)
    .map((h) => h.slice(0, 64));
}

/** Map API commitHashes (object or legacy array) into per-repo draft rows. */
export function commitHashesToDraftEntries(
  raw: unknown,
  parentLinks: string[],
): string[][] {
  const out = parentLinks.map((): string[] => []);
  if (raw == null) return out;

  if (typeof raw === "object" && !Array.isArray(raw)) {
    const map = raw as Record<string, unknown>;
    parentLinks.forEach((url, i) => {
      const hashes = normalizeHashesList(map[url]);
      if (hashes.length) {
        out[i] = hashes;
        return;
      }
      const altKey = Object.keys(map).find(
        (k) => k.trim().toLowerCase() === url.trim().toLowerCase(),
      );
      if (altKey) out[i] = normalizeHashesList(map[altKey]);
    });
    return out;
  }

  if (Array.isArray(raw)) {
    raw.forEach((entry, i) => {
      if (i >= out.length) return;
      if (Array.isArray(entry)) {
        out[i] = normalizeHashesList(entry);
      } else if (typeof entry === "string" && entry.trim()) {
        out[i] = entry
          .split(/[\n\r,]+/)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((h) => h.slice(0, 64));
      }
    });
  }
  return out;
}

/** Build commitHashes object keyed by parent job GitHub URL. */
export function buildCommitHashesPayload(
  parentLinks: string[],
  draftEntries: string[][],
): SubtaskCommitHashesMap | null {
  if (!parentLinks.length) return null;
  const out: SubtaskCommitHashesMap = {};
  parentLinks.forEach((url, i) => {
    const hashes = normalizeHashesList(draftEntries[i] ?? []);
    if (hashes.length) out[url] = hashes;
  });
  return Object.keys(out).length > 0 ? out : null;
}

export function buildSubtaskExtrasBody(
  verificationEndpoints: string[] | null,
  commitHashes: SubtaskCommitHashesMap | null,
): SubtaskExtrasBody | undefined {
  const body: SubtaskExtrasBody = {};
  if (verificationEndpoints?.length) {
    body.verificationEndpoints = verificationEndpoints;
  }
  if (commitHashes && Object.keys(commitHashes).length > 0) {
    body.commitHashes = commitHashes;
  }
  return Object.keys(body).length > 0 ? body : undefined;
}
